import type { ReadonlyNonEmptyArray, ReadonlyRecord } from '../../../utils'

import { Capability, type ConnectwareError, type CybusService } from '../../../domain'

import { type BackendJsonResponseContent, createNonCountMapper, type DeviationFetcher, type ServiceDeviationsSupportedType } from '../../Connectware'
import type { HttpRequestArgs } from '..'
import { type NextBatch, RSTBatchedConnectwareHTTPService, type RSTBatchFetchingStrategy } from './Base'

type DeviationFetchingArgs = Readonly<{ type: ServiceDeviationsSupportedType, serviceId: CybusService['id'] }>
type SupportedEndpoints = '/api/v2/resources/states/deviations/count'
type DeviationsCountBatch = ReadonlyRecord<CybusService['id'], number>

export class RSTDeviationHTTPService
    extends RSTBatchedConnectwareHTTPService<DeviationFetchingArgs, SupportedEndpoints, 'GET', DeviationsCountBatch, number>
    implements DeviationFetcher {
    private readonly mapDeviationsCountData = createNonCountMapper<BackendJsonResponseContent<SupportedEndpoints, 'GET', 200>['data'], DeviationsCountBatch>(
        (data) => data.reduce<DeviationsCountBatch>((r, { serviceId, count }) => ({ ...r, [serviceId]: count }), {})
    )

    private readonly isDeviatedInteraction: Pick<RSTBatchFetchingStrategy<DeviationFetchingArgs, SupportedEndpoints, 'GET'>, 'buildQueryParams'> = {
        buildQueryParams: (args) => ({ ids: args.map(({ serviceId }) => serviceId), itemsPerPage: `${this.pageSize}` }),
    }

    /**
     * These strategies here serve two purposes
     * - Allow for the extraction of deviations count to be possible by many means
     * - Enables the scripts that check for endpoint usage (and associate them with capabilities) to detect these endpoints' usage properly
     */
    private readonly isDeviatedStrategies: ReadonlyRecord<
        DeviationFetchingArgs['type'],
        Pick<RSTBatchFetchingStrategy<DeviationFetchingArgs, SupportedEndpoints, 'GET'>, 'method' | 'path' | 'capability'>
    > = {
        connections: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.CONNECTIONS_READ },
        endpoints: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.ENDPOINTS_READ },
        mappings: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.MAPPINGS_READ },
        nodes: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.NODES_READ },
        resourceDeviation: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.DEVIATIONS_READ },
        servers: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVERS_READ },
        service: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVICE_READ },
        services: { method: 'GET', path: '/api/v2/resources/states/deviations/count', capability: Capability.SERVICES_READ },
    }

    protected resolveBatchFetchingStrategy (args: DeviationFetchingArgs): RSTBatchFetchingStrategy<DeviationFetchingArgs, SupportedEndpoints, 'GET'> {
        return { ...this.isDeviatedStrategies[args.type], ...this.isDeviatedInteraction }
    }

    protected createRequestArgs ({
        strategy: { buildQueryParams, ...strategy },
        requests,
    }: NextBatch<DeviationFetchingArgs, SupportedEndpoints, 'GET', number>): HttpRequestArgs<SupportedEndpoints, 'GET', DeviationsCountBatch> {
        return {
            ...strategy,
            authenticate: true,
            queryParams: buildQueryParams(requests.map((r) => r.args) as readonly DeviationFetchingArgs[] as ReadonlyNonEmptyArray<DeviationFetchingArgs>),
            handlers: {
                200: (response) => response.getJson().then(({ data }) => this.mapDeviationsCountData(data)),
                503: RSTDeviationHTTPService.createServerNotAvailableHandler(),
            },
        }
    }

    // eslint-disable-next-line class-methods-use-this
    protected resolveBatchResponse (result: DeviationsCountBatch, { serviceId }: DeviationFetchingArgs): number | ConnectwareError {
        return result[serviceId] ?? 0
    }

    fetchServiceDeviationsCount (type: ServiceDeviationsSupportedType, serviceId: CybusService['id']): Promise<number> {
        return this.fetchBatched({ type, serviceId })
    }
}
